import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../components/PrimaryButton';
import SpinnerOverlay from '../components/SpinnerOverlay';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';
import rabi1Image from '../assets/rabi1.webp';

// Color constants (matching app theme)
const BLUE_ACCENT = '#3B7BF4';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

const NUSACH_OPTIONS = ['sfarad', 'ashkenaz', 'yamen', 'moroccan'] as const;

const RabbiLoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl =
    i18n.language?.toLowerCase().startsWith('he') || i18n.language?.toLowerCase().startsWith('iw');
  const dir = isRtl ? 'rtl' : 'ltr';
  const [username, setUsername] = useState('');
  const [nusach, setNusach] = useState<string>('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('rabbiToken');
    if (storedToken) {
      // If a token exists, assume the rabbi is already logged in
      navigate('/rabbi');
    }
  }, [navigate]);

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // Use Firebase Functions endpoint (automatically switches between dev/prod)
      const API_ENDPOINT = FIREBASE_FUNCTIONS.rabbiLogin();

      const response = await callCloudFunction('rabbiLogin', API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Origin: window.location.origin,
          Accept: 'application/json',
        },
        body: JSON.stringify({ username, password, nusach: nusach || undefined }),
      });

      const data = await response.json();

      if (response.ok && data.success === true) {
        // Store the bearer token in localStorage
        if (data.token) {
          localStorage.setItem('rabbiToken', data.token);
        }
        // Store rabbi info (name, nusach) in localStorage
        if (data.rabbi) {
          localStorage.setItem('rabbiInfo', JSON.stringify(data.rabbi));
        }
        // Navigate to rabbi upload page on success
        navigate('/rabbi');
      } else {
        const message =
          data.errorCode === 'NUSACH_NOT_AVAILABLE'
            ? t('rabbi_login_nusach_not_available')
            : data.error || t('invalid_credentials');
        setError(message);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('connection_failed') || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleLogin();
  };

  return (
    <div
      className="rabbi-login-page"
      dir={dir}
      style={{
        flex: 1,
        minHeight: '100vh',
        overflow: 'auto',
        background: '#FFFFFF',
        color: TEXT_DARK,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
        fontFamily: 'system-ui, sans-serif',
        padding: '20px',
        position: 'relative',
      }}
    >
      {loading && <SpinnerOverlay />}
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          marginTop: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <img
            src={rabi1Image}
            alt=""
            style={{
              width: '100%',
              maxWidth: 100,
              height: 'auto',
              margin: '0 auto 16px',
              display: 'block',
            }}
          />
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 600,
              color: TEXT_DARK,
            }}
          >
            {t('rabbi_portal_title')}
          </h1>
          <p style={{ margin: '12px 0 0', fontSize: 14, color: TEXT_SECONDARY }}>
            {t('rabbi_login_credentials_hint')}
          </p>
        </div>

        <form
          onSubmit={handleFormSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div>
            <label
              htmlFor="username"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: TEXT_DARK,
              }}
            >
              {t('username')}
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: `1px solid ${BORDER_LIGHT}`,
                background: '#FFFFFF',
                color: TEXT_DARK,
                fontSize: 16,
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = BLUE_ACCENT;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = BORDER_LIGHT;
              }}
              placeholder={t('enter_username') || 'Enter username'}
            />
          </div>

          <div>
            <label
              htmlFor="nusach"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: TEXT_DARK,
              }}
            >
              {t('rabbi_login_nusach')}
            </label>
            <select
              id="nusach"
              value={nusach}
              onChange={(e) => setNusach(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: `1px solid ${BORDER_LIGHT}`,
                background: '#FFFFFF',
                color: TEXT_DARK,
                fontSize: 16,
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = BLUE_ACCENT;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = BORDER_LIGHT;
              }}
            >
              <option value="">{t('rabbi_login_nusach_placeholder')}</option>
              {NUSACH_OPTIONS.map((key) => (
                <option key={key} value={key}>
                  {t(`tradition.${key}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="password"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: TEXT_DARK,
              }}
            >
              {t('password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: `1px solid ${BORDER_LIGHT}`,
                background: '#FFFFFF',
                color: TEXT_DARK,
                fontSize: 16,
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = BLUE_ACCENT;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = BORDER_LIGHT;
              }}
              placeholder={t('enter_password') || 'Enter password'}
            />
          </div>

          {error && (
            <div
              style={{
                padding: '12px',
                borderRadius: 8,
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#DC2626',
                fontSize: 14,
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          <PrimaryButton
            onClick={handleLogin}
            text={loading ? t('logging_in') : t('login')}
            variant="blue"
          />
        </form>

        {/*<PrimaryButton*/}
        {/*  onClick={() => navigate("/register-rabbi-request")}*/}
        {/*  text={t("rabbi_not_registered_register")}*/}
        {/*  variant="white"*/}
        {/*/>*/}
        <PrimaryButton
          onClick={() => navigate('/onboarding')}
          text={t('back_to_onboarding')}
          variant="white"
        />
      </div>
    </div>
  );
};

export default RabbiLoginPage;
